import { createSlice, PayloadAction } from "@reduxjs/toolkit";

type ChatViewType = {
  type: "none" | "chat",
  chatId?: string,
  tempChatData?: any,  // Temporary, will be cleared after new chatlist is loaded
};

type SearchResponseStatus = 'loading' | 'received' | 'noSearchValue';

export interface TeamInboxState {
  filteredChatAndContactList: {
    contacts: string[];
    searchResponseStatus: SearchResponseStatus;
  };
  chatViewMode: ChatViewType;
  hasMoreChatsToLoad: boolean;
  hasMoreMsgsToLoad: boolean;
  chatsSidebarSearchToken: string;
  waSessionPhoneNumber: string | null;
  isArchivedView: boolean;
  // Channels (newsletter) view — like Archived, a special list shown in place
  // of the main inbox. Mutually exclusive with isArchivedView.
  isChannelsView: boolean;
  // Message currently staged to be forwarded. Set by the message hover-action
  // "forward" button; a wave-2 agent builds the picker dialog that consumes it.
  // Typed `any` on purpose — it holds a raw cache message entity.
  forwardingMessage: any | null;
  // Message currently being replied to (WA reply flow). Rendered as the
  // composer's reply preview bar; consumed at send time as
  // userMessage.quotedMessageId. Raw cache message entity, like forwardingMessage.
  replyingToMessage: any | null;
}

const initialState: TeamInboxState = {
  filteredChatAndContactList: {
    contacts: [],
    searchResponseStatus: 'noSearchValue',
  },
  chatViewMode: {
    type: "none",
    chatId: undefined,
    tempChatData: undefined,
  },
  hasMoreMsgsToLoad: false,
  hasMoreChatsToLoad: false,
  chatsSidebarSearchToken: '',
  waSessionPhoneNumber: null,
  isArchivedView: false,
  isChannelsView: false,
  forwardingMessage: null,
  replyingToMessage: null,
};

const TeamInboxSlice = createSlice({
  name: "TeamInbox",
  initialState,
  reducers: {
    setHasMoreChatsToLoad(state, { payload }: PayloadAction<boolean>) {
      state.hasMoreChatsToLoad = payload;
    },
    setHasMoreMsgsToLoad(state, { payload }: PayloadAction<boolean>) {
      state.hasMoreMsgsToLoad = payload;
    },
    resetChatViewMode(state) {
      state.chatViewMode = {
        type: "none",
        chatId: undefined,
        tempChatData: undefined,
      };
    },
    handleClickOnChat(state, action: PayloadAction<ChatViewType>) {
      state.hasMoreMsgsToLoad = false;
      state.chatViewMode = action.payload;
      // A reply draft is chat-scoped — switching chats discards it (WA behavior).
      state.replyingToMessage = null;
    },
    setFilteredChatAndContactList(state, { payload }: PayloadAction<{ contacts: any[], searchResponseStatus?: SearchResponseStatus }>) {
      state.filteredChatAndContactList = {
        contacts: payload.contacts,
        searchResponseStatus: payload.searchResponseStatus ?? state.filteredChatAndContactList.searchResponseStatus,
      };
    },
    resetFilteredChatAndContactList(state) {
      state.filteredChatAndContactList = {
        contacts: [],
        searchResponseStatus: 'noSearchValue',
      };
    },
    preserveChatInTempData(state, action: PayloadAction<{ chatId: string; chatData: any }>) {
      const { chatId, chatData } = action.payload;
      if (state.chatViewMode.type === "chat" && state.chatViewMode.chatId === chatId) {
        state.chatViewMode.tempChatData = chatData;
      }
    },
    clearTempChatData(state) {
      if (state.chatViewMode.tempChatData) {
        state.chatViewMode.tempChatData = undefined;
      }
    },
    setChatsSidebarSearchToken(state, { payload }: PayloadAction<string>) {
      state.chatsSidebarSearchToken = payload;
    },
    resetChatsSidebarSearchToken(state) {
      state.chatsSidebarSearchToken = '';
    },
    setWaSessionPhoneNumber(state, { payload }: PayloadAction<string | null>) {
      state.waSessionPhoneNumber = payload;
    },
    setIsArchivedView(state, { payload }: PayloadAction<boolean>) {
      state.isArchivedView = payload;
      if (payload) state.isChannelsView = false;
    },
    setIsChannelsView(state, { payload }: PayloadAction<boolean>) {
      state.isChannelsView = payload;
      if (payload) state.isArchivedView = false;
    },
    setForwardingMessage(state, { payload }: PayloadAction<any | null>) {
      state.forwardingMessage = payload;
    },
    setReplyingToMessage(state, { payload }: PayloadAction<any | null>) {
      state.replyingToMessage = payload;
    },
  },
});

export const TeamInboxActions = TeamInboxSlice.actions;

export default TeamInboxSlice.reducer;